import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Subject } from 'rxjs';
import Utils from '@core/utils/utils';

/**
 * Created date: 2018/11/21
 * Provides a wrapper for accessing the web storage API and synchronizing session storage
 * across tabs/windows.
 */
@Injectable({
  providedIn: 'root'
})
export class LocalStoreManagerService {
  public static readonly DBKEY_USER_DATA = 'user_data';
  private static readonly DBKEY_SYNC_KEYS = 'sync_keys';
  private static syncListenerInitialised = false;
  private static reservedKeys: string[] =
    [
      'sync_keys',
      'addToSyncKeys',
      'removeFromSyncKeys',
      'getSessionStorage',
      'setSessionStorage',
      'addToSessionStorage',
      'removeFromSessionStorage',
      'clearAllSessionsStorage'
    ];

  public loginPageURL = '/pl/login';

  constructor(
    private router: Router
  ) {
  }

  private syncKeys: string[] = [];
  private initEvent = new Subject();

  /**
   * Initialises storage sync listener
   * @returns null
   */
  public initialiseStorageSyncListener() {
    if (LocalStoreManagerService.syncListenerInitialised === true) {
      return;
    }

    LocalStoreManagerService.syncListenerInitialised = true;

    // Add storage event on windown
    window.addEventListener('storage', this.sessionStorageTransferHandler, false);
    this.syncSessionStorage();
  }

  /**
   * Deinitialises storage sync listener
   */
  public deinitialiseStorageSyncListener() {
    // Remove storage evemt on windown
    window.removeEventListener('storage', this.sessionStorageTransferHandler, false);

    LocalStoreManagerService.syncListenerInitialised = false;
  }

  private sessionStorageTransferHandler = (event: StorageEvent) => {
    // do nothing if no value to work with
    if (!event.newValue) {
      return;
    }

    if (event.key === 'getSessionStorage') {
      if (sessionStorage.length) {
        // another tab asked for the sessionStorage -> send it
        this.localStorageSetItem('setSessionStorage', sessionStorage);

        // the other tab should now have it, so we're done with it.
        // could do short timeout as well.
        localStorage.removeItem('setSessionStorage');
      }
    } else if (event.key === 'setSessionStorage') {
      // another tab sent data <- get it
      if (!this.syncKeys.length) {
        this.loadSyncKeys();
      }

      const data = JSON.parse(event.newValue);
      // console.info('Set => Key: Transfer setSessionStorage' + ',  data: ' + JSON.stringify(data));

      for (const key in data) {
        if (this.syncKeysContains(key)) {
          this.sessionStorageSetItem(key, JSON.parse(data[key]));
        }
      }

      this.onInit();
    } else if (event.key === 'addToSessionStorage') {
      const data = JSON.parse(event.newValue);
      // console.warn('Set => Key: Transfer addToSessionStorage' + ',  data: ' + JSON.stringify(data));
      // tslint:disable-next-line:no-string-literal
      this.addToSessionStorageHelper(data['data'], data['key']);
      // tslint:disable-next-line:no-string-literal
      if (data['key'] === 'SI') {
        if (!this.router.url.includes(this.loginPageURL)) {
          location.reload();
        }
      }
    } else if (event.key === 'removeFromSessionStorage') {
      this.removeFromSessionStorageHelper(event.newValue);
    } else if (event.key === 'clearAllSessionsStorage' && sessionStorage.length) {
      this.clearInstanceSessionStorage();
    } else if (event.key === 'addToSyncKeys') {
      this.addToSyncKeysHelper(event.newValue);
    } else if (event.key === 'removeFromSyncKeys') {
      this.removeFromSyncKeysHelper(event.newValue);
    }
  }

  private syncSessionStorage() {
    localStorage.setItem('getSessionStorage', '_dummy');
    localStorage.removeItem('getSessionStorage');
  }

  /**
   * Clears all storage
   */
  public clearAllStorage() {
    this.clearAllSessionsStorage();
    this.clearLocalStorage();
  }

  /**
   * Clears all sessions storage
   */
  public clearAllSessionsStorage() {
    this.clearInstanceSessionStorage();

    // Remove DBKEY_SYNC_KEYS
    localStorage.removeItem(LocalStoreManagerService.DBKEY_SYNC_KEYS);

    // active trigger 'clearAllSessionsStorage' on other browser
    localStorage.setItem('clearAllSessionsStorage', '_dummy');
    localStorage.removeItem('clearAllSessionsStorage');
  }

  /**
   * Clears instance session storage
   */
  public clearInstanceSessionStorage() {
    sessionStorage.clear();
    this.syncKeys = [];
  }

  /**
   * Clears local storage
   */
  public clearLocalStorage() {
    localStorage.clear();
  }

  private syncKeysContains(key: string) {
    return this.syncKeys.some(x => x === key);
  }

  private loadSyncKeys() {
    if (this.syncKeys.length) {
      return;
    }

    this.syncKeys = this.getSyncKeysFromStorage();
  }

  private getSyncKeysFromStorage(defaultValue: string[] = []): string[] {

    const data = this.localStorageGetItem(LocalStoreManagerService.DBKEY_SYNC_KEYS);

    if (data == null) {
      return defaultValue;
    } else {
      return data as string[];
    }
  }

  private removeFromSyncKeysBackup(key: string) {

    const storedSyncKeys = this.getSyncKeysFromStorage();

    const index = storedSyncKeys.indexOf(key);

    if (index > -1) {
      storedSyncKeys.splice(index, 1);
      this.localStorageSetItem(LocalStoreManagerService.DBKEY_SYNC_KEYS, storedSyncKeys);
    }
  }

  private addToSyncKeysHelper(key: string) {
    if (!this.syncKeysContains(key)) {
      this.syncKeys.push(key);
    }
  }

  private removeFromSyncKeys(key: string) {
    this.removeFromSyncKeysHelper(key);
    this.removeFromSyncKeysBackup(key);

    localStorage.setItem('removeFromSyncKeys', key);
    localStorage.removeItem('removeFromSyncKeys');
  }

  private removeFromSyncKeysHelper(key: string) {
    const index = this.syncKeys.indexOf(key);

    if (index > -1) {
      this.syncKeys.splice(index, 1);
    }
  }

  /**
   * Saves session data
   */
  public saveSessionData(data: any, key = LocalStoreManagerService.DBKEY_USER_DATA) {
    this.testForInvalidKeys(key);
    this.removeFromSyncKeys(key);
    localStorage.removeItem(key);
    this.sessionStorageSetItem(key, data);
  }

  public saveSyncedSessionData(data: any, key = LocalStoreManagerService.DBKEY_USER_DATA) {
    this.testForInvalidKeys(key);
    localStorage.removeItem(key);
    this.addToSessionStorage(data, key);
  }

  private addToSessionStorage(data: any, key: string) {
    this.addToSessionStorageHelper(data, key);
    this.addToSyncKeysBackup(key);

    this.localStorageSetItem('addToSessionStorage', { key, data });
    localStorage.removeItem('addToSessionStorage');
  }

  private addToSyncKeysBackup(key: string) {
    const storedSyncKeys = this.getSyncKeysFromStorage();

    if (!storedSyncKeys.some(x => x === key)) {
      storedSyncKeys.push(key);
      this.localStorageSetItem(LocalStoreManagerService.DBKEY_SYNC_KEYS, storedSyncKeys);
    }
  }

  private addToSessionStorageHelper(data: any, key: string) {
    this.addToSyncKeysHelper(key);
    this.sessionStorageSetItem(key, data);
  }

  private sessionStorageSetItem(key: string, data: any) {
    sessionStorage.setItem(key, JSON.stringify(data));
  }

  public savePermanentData(data: any, key = LocalStoreManagerService.DBKEY_USER_DATA) {
    this.testForInvalidKeys(key);
    this.removeFromSessionStorage(key);
    this.localStorageSetItem(key, data);
    this.initEvent.next('changed');
  }

  private localStorageSetItem(key: string, data: any) {
    localStorage.setItem(key, JSON.stringify(data));
  }

  private removeFromSessionStorage(keyToRemove: string) {
    this.removeFromSessionStorageHelper(keyToRemove);
    this.removeFromSyncKeysBackup(keyToRemove);

    localStorage.setItem('removeFromSessionStorage', keyToRemove);
    localStorage.removeItem('removeFromSessionStorage');
  }

  private removeFromSessionStorageHelper(keyToRemove: string) {
    sessionStorage.removeItem(keyToRemove);
    this.removeFromSyncKeysHelper(keyToRemove);
  }

  public moveDataToSessionStorage(key = LocalStoreManagerService.DBKEY_USER_DATA) {
    this.testForInvalidKeys(key);

    const data = this.getData(key);

    if (data == null) { return; }

    this.saveSessionData(data, key);
  }

  public moveDataToSyncedSessionStorage(key = LocalStoreManagerService.DBKEY_USER_DATA) {

    this.testForInvalidKeys(key);

    const data = this.getData(key);

    if (data == null) {
      return;
    }

    this.saveSyncedSessionData(data, key);
  }

  public moveDataToPermanentStorage(key = LocalStoreManagerService.DBKEY_USER_DATA) {

    this.testForInvalidKeys(key);

    const data = this.getData(key);

    if (data == null) {
      return;
    }

    this.savePermanentData(data, key);
  }

  public exists(key = LocalStoreManagerService.DBKEY_USER_DATA) {

    let data = sessionStorage.getItem(key);

    if (data == null) { data = localStorage.getItem(key); }

    return data != null;
  }

  public getData(key = LocalStoreManagerService.DBKEY_USER_DATA) {

    this.testForInvalidKeys(key);

    let data = this.sessionStorageGetItem(key);

    if (data == null) {
      data = this.localStorageGetItem(key);
    }

    return data;
  }

  private localStorageGetItem(key: string) {
    return Utils.JSonTryParse(localStorage.getItem(key));
  }

  private sessionStorageGetItem(key: string) {
    return Utils.JSonTryParse(sessionStorage.getItem(key));
  }

  public getDataObject<T>(key = LocalStoreManagerService.DBKEY_USER_DATA, isDateType = false): T {

    let data = this.getData(key);

    if (data != null) {
      if (isDateType) {
        data = new Date(data);
      }

      return data as T;
    } else {
      return null;
    }
  }

  public deleteData(key = LocalStoreManagerService.DBKEY_USER_DATA) {
    this.testForInvalidKeys(key);

    this.removeFromSessionStorage(key);
    localStorage.removeItem(key);
    // this.initEvent.next('changed');
  }

  private testForInvalidKeys(key: string) {
    if (!key) {
      throw new Error('key cannot be empty');
    }

    if (LocalStoreManagerService.reservedKeys.some(x => x === key)) {
      throw new Error(`The storage key '${key}' is reserved and cannot be used. Please use a different key`);
    }
  }

  private onInit() {
    setTimeout(() => {
      this.initEvent.next();
      this.initEvent.complete();
    });
  }

  public getInitEvent(): Observable<{}> {
    return this.initEvent.asObservable();
  }
}
